import { SumitStatusBadge } from '@/components/common'
import type { SumitStatus } from '@/components/common'
import type { Document } from '@/types'
import { getSumitDocumentUrl } from '@/utils'

interface SumitImportSource {
  upload_date?: string
  file_url?: string
  employee?: string | null
  comments?: string | null
  original_status?: string | null
  return_reason?: string | null
  import_s3_key?: string
  imported_at?: string
  row_number?: number
}

interface SumitMetadataProps {
  document: Document
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function SumitMetadata({ document }: SumitMetadataProps) {
  const sumitStatus = (document.sumit_status ?? document.extracted_metadata?.sumit_status) as SumitStatus | undefined
  const sumitUrl = getSumitDocumentUrl(document)

  // Nothing to show if neither a Sumit status nor a Sumit web link is present.
  if (!sumitStatus && !sumitUrl) return null

  const sumitComment = (document.sumit_comment ?? document.extracted_metadata?.sumit_comment) as string | undefined
  const source = document.extracted_metadata?.sumit_import_source as SumitImportSource | undefined

  return (
    <div className="space-y-3" data-testid="sumit-metadata">
      <h3 className="text-sm font-medium text-gray-700">Sumit Import</h3>

      <dl className="space-y-2 text-sm">
        {sumitStatus && (
          <div className="flex items-center justify-between">
            <dt className="text-gray-500">Status</dt>
            <dd><SumitStatusBadge status={sumitStatus} /></dd>
          </div>
        )}

        {sumitUrl && (
          <div className="flex items-center justify-between">
            <dt className="text-gray-500">Sumit Page</dt>
            <dd>
              <a
                href={sumitUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 font-medium"
                data-testid="sumit-document-link"
              >
                View in Sumit ↗
              </a>
            </dd>
          </div>
        )}

        {sumitComment && (
          <div>
            <dt className="text-gray-500">Comment</dt>
            <dd className="mt-0.5 text-gray-900">{sumitComment}</dd>
          </div>
        )}

        {source?.upload_date && (
          <div className="flex items-center justify-between">
            <dt className="text-gray-500">Upload Date</dt>
            <dd className="text-gray-900">{formatDate(source.upload_date)}</dd>
          </div>
        )}

        {source?.employee && (
          <div className="flex items-center justify-between">
            <dt className="text-gray-500">Employee</dt>
            <dd className="text-gray-900">{source.employee}</dd>
          </div>
        )}

        {source?.original_status && (
          <div className="flex items-center justify-between">
            <dt className="text-gray-500">Original Status</dt>
            <dd className="text-gray-900">{source.original_status}</dd>
          </div>
        )}

        {source?.return_reason && (
          <div className="flex items-center justify-between">
            <dt className="text-gray-500">Return Reason</dt>
            <dd className="text-gray-900">{source.return_reason}</dd>
          </div>
        )}
      </dl>
    </div>
  )
}
